import useAppStore from '@views/app.store'
// --- 1. 类型定义 ---

export interface RuleConfig {
  label: string
  value: string // 对应输出的 id
  rule: ('prefix' | 'main' | 'suffix' | 'suffix2')[]
}

export interface KeywordData {
  prefix: string[]
  main: string[] // 对应输出的 baseWord
  suffix: string[]
  suffix2: string[]
  [key: string]: string[]
}

// 输出结构
export interface ResultItem {
  keyword: string // 组合出来的完整词 (已处理空格)
  baseWord: string // 使用的主词
  id: string // 规则ID
}

// --- 2. 核心逻辑类 ---

export default class ComboGenerator {
  private rules: RuleConfig[]

  private limit: number

  constructor(rules: RuleConfig[], limit = 500) {
    this.rules = rules
    this.limit = limit
  }

  /**
   * 【新增】智能拼接函数
   * 逻辑：如果所有片段都是纯中文，则无缝拼接；如果包含英文/数字/符号，则用空格拼接。
   */
  private smartJoin(parts: string[]): string {
    // 正则匹配纯中文字符 (根据你的参考逻辑)
    // const isChineseRegex = /^[\u4e00-\u9fa5]+$/

    // 检查是否所有部分都只是中文
    // const allChinese = parts.every(item => isChineseRegex.test(item))

    // return allChinese ? parts.join('') : parts.join(' ')
    return useAppStore().geoMode === 'outer' ? parts.join(' ') : parts.join('')
  }

  /**
   * 辅助：计算单条规则的理论最大组合数
   */
  private getRuleCapacity(rule: RuleConfig, data: KeywordData): number {
    let capacity = 1
    let isValid = true
    rule.rule.forEach(field => {
      const count = data[field]?.length || 0
      if (count === 0) isValid = false
      capacity *= count
    })
    return isValid ? capacity : 0
  }

  /**
   * 辅助：随机获取一个元素
   */
  private getRandomItem<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)]
  }

  /**
   * 核心辅助：生成单条数据
   * 变更点：使用 this.smartJoin 替代 join('')
   */
  private generateSingleItem(
    rule: RuleConfig,
    forcedMainWord: string,
    data: KeywordData,
  ): ResultItem {
    const parts = rule.rule.map(field => {
      if (field === 'main') {
        return forcedMainWord
      }
      return this.getRandomItem(data[field] || [])
    })

    return {
      keyword: this.smartJoin(parts), // <--- 这里使用了智能拼接
      baseWord: forcedMainWord,
      id: rule.value,
    }
  }

  /**
   * 模式 A: 穷举所有组合
   * 变更点：使用 this.smartJoin
   */
  private generateAll(data: KeywordData, capacities: Map<string, number>): ResultItem[] {
    const results: ResultItem[] = []

    this.rules.forEach(ruleItem => {
      if (capacities.get(ruleItem.value) === 0) return

      const generateRecursive = (
        fieldIndex: number,
        currentParts: string[],
        currentBaseWord: string,
      ) => {
        if (fieldIndex >= ruleItem.rule.length) {
          results.push({
            keyword: this.smartJoin(currentParts), // <--- 这里使用了智能拼接
            baseWord: currentBaseWord,
            id: ruleItem.value,
          })
          return
        }

        const fieldKey = ruleItem.rule[fieldIndex]
        const words = data[fieldKey] || []

        words.forEach(word => {
          const newBaseWord = fieldKey === 'main' ? word : currentBaseWord
          generateRecursive(fieldIndex + 1, [...currentParts, word], newBaseWord)
        })
      }

      generateRecursive(0, [], '')
    })

    return results
  }

  /**
   * 模式 B: 智能随机 (主词保底 + 权重填充)
   */
  private generateSmartRandom(
    data: KeywordData,
    capacities: Map<string, number>,
    totalCapacity: number,
  ): ResultItem[] {
    const results: ResultItem[] = []
    const generatedKeys = new Set<string>() // 去重池：存储生成的 keyword

    const allMainWords = data.main || []

    // --- Phase 1: 主词保底覆盖 ---
    for (const mainWord of allMainWords) {
      if (results.length >= this.limit) break

      const validRules = this.rules.filter(
        r => r.rule.includes('main') && (capacities.get(r.value) || 0) > 0,
      )

      if (validRules.length > 0) {
        const randomRule = this.getRandomItem(validRules)
        const item = this.generateSingleItem(randomRule, mainWord, data)

        if (!generatedKeys.has(item.keyword)) {
          results.push(item)
          generatedKeys.add(item.keyword)
        }
      }
    }

    // --- Phase 2: 剩余名额权重填充 ---
    const remainingQuota = this.limit - results.length

    if (remainingQuota > 0) {
      const quotas = this.rules.map(rule => {
        const cap = capacities.get(rule.value) || 0
        const weight = totalCapacity > 0 ? cap / totalCapacity : 0
        const targetCount = Math.floor(weight * remainingQuota)
        return { rule, count: targetCount, cap }
      })

      let safetyLoop = 0

      while (results.length < this.limit && safetyLoop < this.limit * 5) {
        safetyLoop++
        const randomRuleIndex = Math.floor(Math.random() * this.rules.length)
        const { rule, cap } = quotas[randomRuleIndex]

        if (cap === 0) continue

        const randomMain = this.getRandomItem(data.main)
        const item = this.generateSingleItem(rule, randomMain, data)

        if (!generatedKeys.has(item.keyword)) {
          results.push(item)
          generatedKeys.add(item.keyword)
        }
      }
    }

    return results
  }

  /**
   * 主入口
   */
  public generate(data: KeywordData): ResultItem[] {
    if (!data.main || data.main.length === 0) return []

    let totalCapacity = 0
    const ruleCapacities = new Map<string, number>()

    this.rules.forEach(rule => {
      const cap = this.getRuleCapacity(rule, data)
      ruleCapacities.set(rule.value, cap)
      totalCapacity += cap
    })

    let results: ResultItem[]

    if (totalCapacity <= this.limit) {
      results = this.generateAll(data, ruleCapacities)
    } else {
      results = this.generateSmartRandom(data, ruleCapacities, totalCapacity)
      // 洗牌
      for (let i = results.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[results[i], results[j]] = [results[j], results[i]]
      }
    }
    return results
  }
}
